import { useEffect, useRef, type MutableRefObject } from "react";
import { toHex } from "@veto/crypto-bridge";
import type { Account, Contact, GroupRecord, MessageEvent, TransportChannel } from "@veto/domain";
import { iceServersWithMintedTurn, type DirectPeerLink, type RelayPool } from "@veto/transport";
import { processInboundRelayEvent, replayInboundJournal, type InboundDeps } from "../messaging/inboundProcessor";
import {
  getSupabaseTransport,
  poolRelays,
  routingHint,
  selfRealtimeTopic,
} from "../messaging/fallbackTransport";
import { directConversationId } from "../messaging/wire";
import type { SessionBundle } from "../messaging/accountLifecycle";
import type { Messenger } from "../messaging/messenger";
import type { InAppBannerPayload } from "../notifications";

export type RelayInboxOptions = {
  screen: string;
  account: Account | null;
  session: SessionBundle | null;
  messenger: Messenger | null;
  relay: RelayPool;
  seenEventIds: Set<string>;
  activeIdRef: MutableRefObject<string | null>;
  contactsRef: MutableRefObject<Contact[]>;
  peersRef: MutableRefObject<Map<string, DirectPeerLink>>;
  voiceUrlsRef: MutableRefObject<Map<string, string>>;
  putMessage: (m: MessageEvent) => Promise<void>;
  insertMessage: (m: MessageEvent) => Promise<boolean>;
  listMessages: (id: string) => Promise<MessageEvent[]>;
  setRelayStatus: (s: string) => void;
  setToast: (t: string | null) => void;
  setBanner: (b: InAppBannerPayload | null) => void;
  setMessages: (m: MessageEvent[] | ((prev: MessageEvent[]) => MessageEvent[])) => void;
  setActiveGroup: (g: GroupRecord | null) => void;
  bumpVoice: () => void;
  refresh: () => Promise<void>;
  /** Exposes the inbound entry point (direct peer frames re-enter here). */
  inboundRef?: MutableRefObject<((ev: unknown, hint?: TransportChannel) => void) | null>;
  /** Bumped when relay preferences change (Settings → Network). */
  netPrefsVersion?: number;
};

export function useRelayInbox(opts: RelayInboxOptions) {
  // Latest callbacks without re-running the subscription effect on every render.
  const latest = useRef(opts);
  latest.current = opts;

  const { screen, account, session, relay, netPrefsVersion } = opts;
  const selfKey = account?.messagingPublicKeyHex ?? null;

  // Pool membership follows network preferences.
  useEffect(() => {
    relay.setRelays(poolRelays());
  }, [relay, netPrefsVersion]);

  useEffect(() => {
    if (screen !== "app" || !account || !session) return;
    // False after cleanup (lock, sign-out): in-flight deliveries stop without marking anything seen.
    let live = true;
    const selfHint = routingHint(account.messagingPublicKeyHex);
    const transportXOnly = toHex(session.identity.transportPublicKey()).toLowerCase();
    relay.setAuth({
      // relay-dev challenge–response (operated lab relays).
      dev: { messagingPublicKeyHex: account.messagingPublicKeyHex, sign: (m) => session.identity.sign(m) },
      // NIP-42 with the inbox (transport) key — the secret stays in the Rust core.
      nip42: { pubkeyHex: transportXOnly, signId: (id) => session.identity.transportSign(id) },
    });
    relay.setInbox({ routingKey: selfHint, p: [transportXOnly], w: [selfHint] });

    const buildDeps = (transportHint?: TransportChannel): InboundDeps => {
      const o = latest.current;
      return {
        account: o.account ?? account,
        identity: session.identity,
        store: session.store,
        seenEventIds: o.seenEventIds,
        activeConversationId: o.activeIdRef.current,
        contacts: o.contactsRef.current,
        peers: o.peersRef.current,
        voiceUrls: o.voiceUrlsRef.current,
        transportHint,
        putMessage: o.putMessage,
        insertMessage: o.insertMessage,
        listMessages: o.listMessages,
        sendTo: async (contact, contentType, body) => {
          const m = latest.current.messenger;
          if (!m) return;
          await m.send({
            conversationId: await directConversationId(account.messagingPublicKeyHex, contact.messagingPublicKeyHex),
            recipients: [contact],
            contentType,
            body,
          });
        },
        // Always the CURRENT session's pipeline: a link outliving a lock must not open frames
        // with freed keys (that silently lost messages).
        onPeerFrame: (data) => {
          const inbound = latest.current.inboundRef?.current;
          if (!inbound) return;
          try {
            inbound(["EVENT", "direct", JSON.parse(data)], "direct");
          } catch {
            /* malformed direct frame */
          }
        },
        iceServers: () => iceServersWithMintedTurn(),
        alive: () => live,
        onToast: o.setToast,
        onBanner: o.setBanner,
        onMessagesAppend: (conversationId, msg) => {
          if (latest.current.activeIdRef.current !== conversationId) return;
          latest.current.setMessages((m) => (m.some((x) => x.eventId === msg.eventId) ? m : [...m, msg]));
        },
        onMessagesReload: async (conversationId) => {
          if (latest.current.activeIdRef.current === conversationId) {
            latest.current.setMessages(await latest.current.listMessages(conversationId));
          }
        },
        onActiveGroup: o.setActiveGroup,
        onVoiceUrl: o.bumpVoice,
        refresh: o.refresh,
      };
    };
    const handleEvent = (ev: unknown, transportHint?: TransportChannel) => {
      void processInboundRelayEvent(ev, buildDeps(transportHint));
    };
    if (opts.inboundRef) opts.inboundRef.current = handleEvent;
    // Finish anything decrypted but not handled before the last shutdown (review M2).
    void replayInboundJournal(buildDeps()).catch(() => undefined);

    const off = relay.onEvent((ev, _url, tier) => handleEvent(ev, tier === "private" ? "relay" : "community"));

    // Honest connectivity indicator (topbar dot).
    const reportStatus = () => {
      const ready = relay.readyCount();
      const rt = getSupabaseTransport()?.isUsable() ?? false;
      latest.current.setRelayStatus(ready >= 2 ? "connected" : ready === 1 || rt ? "degraded" : "offline");
    };
    reportStatus();
    const statusTimer = window.setInterval(reportStatus, 3_000);

    const wake = () => {
      relay.nudge();
      void getSupabaseTransport()?.connect();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") wake();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", onVisible);

    // Serverless last resort: live topic + catch-up of anything parked there.
    const rt = getSupabaseTransport();
    void rt?.connect();
    const selfTopic = selfRealtimeTopic(account.messagingPublicKeyHex);
    const offRealtime = rt?.subscribe([selfTopic], (payload) => {
      try {
        handleEvent(["EVENT", "supabase", JSON.parse(payload)], "realtime");
      } catch {
        /* malformed payload — ignore */
      }
    });
    void rt?.fetchEnvelopes(selfHint).then((envelopes) => {
      for (const payload of envelopes) {
        try {
          handleEvent(["EVENT", "supabase", JSON.parse(payload)], "realtime");
        } catch {
          /* malformed payload — ignore */
        }
      }
    });

    return () => {
      live = false;
      // Locking ends every direct link: no keep-alives revealing address or presence.
      for (const link of latest.current.peersRef.current.values()) link.close();
      latest.current.peersRef.current.clear();
      off();
      offRealtime?.();
      relay.setInbox(null);
      window.clearInterval(statusTimer);
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", onVisible);
      if (opts.inboundRef?.current === handleEvent) opts.inboundRef.current = null;
    };
    // Stable identity inputs only — everything else is read through `latest`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, selfKey, session, relay]);
}
